import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const FadeUp = ({ children, delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(32px)',
      transition: `opacity 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.9s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
};

const sections = [
  {
    id: '01',
    title: 'Who We Are',
    content: `Mino is a prediction markets and forecasting platform operated in Kenya, allowing users to take positions on the outcomes of real-world events using Kenyan Shillings (KES). Mino is not a gambling or betting operator. We are committed to protecting your personal data in full compliance with the Kenya Data Protection Act 2019 (KDPA).\n\nFor any privacy-related queries, contact us at support@minomarkets.app. We will respond within 21 days as required by law.`,
  },
  {
    id: '02',
    title: 'Data We Collect',
    content: 'We collect the following personal data when you register and use Mino:',
    list: [
      'Full name — to identify your account',
      'Email address — for account login, communications, and OTP verification',
      'Phone number — for M-Pesa payment processing',
      'Password — stored as an encrypted bcrypt hash (we never see your real password)',
      'Account balance and transaction history — required to operate the platform',
      'Trading history — records of all positions opened, fees paid, and payouts received',
      'Responsible trading settings — deposit limits and self-exclusion preferences',
      'Device and usage data — browser type, IP address, and session activity',
      'Login security data — failed login attempts and session tokens (for account protection)',
    ],
  },
  {
    id: '03',
    title: 'Why We Collect It',
    content: 'We collect and process your data for the following lawful purposes under the Kenya Data Protection Act 2019:',
    list: [
      'Account creation and management (contractual necessity)',
      'Processing deposits, withdrawals, position payouts, and platform fee deductions via M-Pesa (contractual necessity)',
      'Email OTP verification for account creation and password reset (contractual necessity)',
      'Sending notifications when markets resolve (legitimate interest)',
      'Enforcing responsible trading limits and self-exclusion (legal and contractual obligation)',
      'Detecting and preventing fraud and money laundering (legal obligation)',
      'Complying with Kenyan financial and tax regulations, including KRA withholding tax reporting (legal obligation)',
      'Improving platform performance based on anonymised usage patterns (legitimate interest)',
      'Account security — detecting unauthorized access and login attempts (legitimate interest)',
    ],
  },
  {
    id: '04',
    title: 'How We Store Your Data',
    content: `Your data is stored in a secured database hosted on protected infrastructure. Passwords are never stored in plain text — we use bcrypt encryption with a high security factor.\n\nSession tokens are stored in your browser's session storage and are invalidated server-side upon logout, meaning signed-out tokens cannot be reused even if intercepted. Access to the database is restricted to authorised personnel only.\n\nWe retain your data for as long as your account is active, and for up to 7 years after account closure as required by Kenyan financial regulations.`,
  },
  {
    id: '05',
    title: 'Who We Share It With',
    content: 'We do not sell your personal data under any circumstances. We may share limited data with:',
    list: [
      'M-Pesa / Safaricom — to process payments (phone number and transaction amount only)',
      'Email service provider — to deliver OTP codes and account notifications (email address only)',
      'Hosting and infrastructure providers — who store data on our behalf under strict confidentiality agreements',
      'Error monitoring services (Sentry) — anonymised error and crash data only, no personal data',
      'Kenya Revenue Authority (KRA) — withholding tax remittance on net gains at market resolution, in compliance with Kenyan law',
      'Financial Reporting Centre (FRC) — as required under the Financial Reporting Centre Act for AML compliance',
      'Law enforcement or regulatory bodies — where required by a valid legal order or Kenyan law',
    ],
  },
  {
    id: '06',
    title: 'Responsible Trading Data',
    content: `Mino stores responsible trading preferences including your deposit limits (daily, weekly, monthly) and self-exclusion status. This data is used exclusively to enforce the protections you have set for yourself.\n\nThis data is never shared with third parties except where required by applicable regulatory reporting obligations. You may update your deposit limits at any time from your profile. Self-exclusion settings cannot be reversed during the exclusion period.`,
  },
  {
    id: '07',
    title: 'Your Rights Under KDPA 2019',
    content: 'Under the Kenya Data Protection Act 2019, you have the following rights:',
    list: [
      'Right to access — request a copy of all personal data we hold about you',
      'Right to correction — request that inaccurate or incomplete data be corrected',
      'Right to deletion — request permanent deletion of your account and all associated data',
      'Right to object — object to processing of your data for any purpose beyond contractual necessity',
      'Right to data portability — receive your data in a structured, portable format',
      'Right to withdraw consent — withdraw consent for any processing based on consent',
      'Right to lodge a complaint — with the Office of the Data Protection Commissioner (ODPC) at www.odpc.go.ke',
    ],
    footer: 'To exercise any of these rights, email us at support@minomarkets.app. We will respond within 21 days as required by the KDPA 2019.',
  },
  {
    id: '08',
    title: 'Cookies & Session Storage',
    content: `Mino uses browser sessionStorage to store your login session token. We do not use third-party tracking cookies, advertising networks, or behavioural analytics platforms. We do not share any usage data with advertising platforms.\n\nUpon logout, your session token is invalidated on our servers, ensuring that your session cannot be hijacked or reused after sign-out.`,
  },
  {
    id: '09',
    title: 'Security Measures',
    content: 'Mino implements the following security measures to protect your data:',
    list: [
      'bcrypt password hashing — passwords are never stored or transmitted in plain text',
      'JWT token authentication with server-side invalidation on logout',
      'Account lockout after 5 consecutive failed login attempts (15-minute lock)',
      'Email OTP verification for account creation and password reset',
      'M-Pesa callback verification to prevent fraudulent deposit confirmations',
      'Encrypted database connections and restricted access controls',
      'Rate limiting on all sensitive endpoints to prevent brute-force attacks',
    ],
  },
  {
    id: '10',
    title: 'Platform Nature & Regulatory Status',
    content: `Mino operates as a prediction market and forecasting platform. Users engage with Mino based on research, analysis, and informed judgment about real-world verifiable outcomes. Mino does not operate as a gambling, betting, or gaming platform under the Kenyan Betting, Lotteries and Gaming Act.\n\nMino complies with applicable Kenyan laws including the Data Protection Act 2019, the Financial Reporting Centre Act, and relevant financial regulations. Mino reserves the right to restrict access to users in jurisdictions where prediction markets are not permitted.`,
  },
  {
    id: '11',
    title: 'Changes to This Policy',
    content: `We may update this Privacy Policy from time to time to reflect changes in our practices or legal obligations. We will notify you of significant changes via email or an in-app notification at least 14 days before changes take effect.\n\nContinued use of Mino after changes constitutes acceptance of the updated policy.`,
  },
  {
    id: '12',
    title: 'Contact & Complaints',
    content: 'For privacy questions, data requests, or complaints:',
    list: [
      'Email: support@minomarkets.app',
      'Legal: support@minomarkets.app',
      'Office of the Data Protection Commissioner (ODPC): www.odpc.go.ke',
    ],
    footer: 'Last updated: April 2026',
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [heroRef, heroVisible] = useInView(0.05);

  return (
    <div style={{ minHeight: '100vh', background: '#000000', fontFamily: "'Outfit', sans-serif", color: '#e8e6e0', position: 'relative', overflowX: 'hidden' }}>
      <Helmet>
        <title>Privacy Policy — Mino Markets</title>
        <link href="https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&family=Outfit:wght@300;400;500;600&display=swap" rel="stylesheet" />
        <meta name="description" content="How Mino Markets collects, uses, and protects your personal data under the Kenya Data Protection Act 2019." />
      </Helmet>

      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::selection { background: rgba(255,255,255,0.12); }
        ::-webkit-scrollbar { width: 3px; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 2px; }

        .mino-aurora {
          position: fixed; inset: 0; pointer-events: none; z-index: 0; overflow: hidden;
        }
        .mino-aurora::before {
          content: '';
          position: absolute;
          width: 140%; height: 55%;
          top: -18%; left: -20%;
          background: conic-gradient(from 200deg at 50% 50%,
            transparent 0deg,
            rgba(55,114,255,0.09) 40deg,
            rgba(22,199,132,0.07) 80deg,
            rgba(124,58,237,0.08) 120deg,
            transparent 160deg
          );
          filter: blur(60px);
          animation: auroraShift 20s ease-in-out infinite alternate;
          transform-origin: center top;
        }
        .mino-aurora::after {
          content: '';
          position: absolute;
          width: 80%; height: 40%;
          bottom: 5%; right: -10%;
          background: conic-gradient(from 30deg at 50% 50%,
            transparent 0deg,
            rgba(22,199,132,0.06) 50deg,
            rgba(55,114,255,0.05) 100deg,
            transparent 150deg
          );
          filter: blur(70px);
          animation: auroraShift2 26s ease-in-out infinite alternate;
          transform-origin: center bottom;
        }
        .mino-dot-grid {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: radial-gradient(circle, rgba(255,255,255,0.045) 1px, transparent 1px);
          background-size: 40px 40px;
          mask-image: radial-gradient(ellipse 85% 85% at 50% 40%, black 40%, transparent 100%);
        }
        .mino-beam {
          position: fixed; pointer-events: none; z-index: 0;
          width: 1px; top: 0; bottom: 0;
          background: linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.04) 30%, rgba(255,255,255,0.06) 60%, transparent 100%);
          animation: beamDrift var(--dur, 28s) ease-in-out infinite alternate;
        }
        @keyframes auroraShift {
          0%   { transform: rotate(-4deg) scaleX(1);    opacity: 1; }
          50%  { transform: rotate(2deg)  scaleX(1.08); opacity: 0.85; }
          100% { transform: rotate(-6deg) scaleX(0.96); opacity: 1; }
        }
        @keyframes auroraShift2 {
          0%   { transform: rotate(6deg)  scaleX(1);    opacity: 0.8; }
          50%  { transform: rotate(-3deg) scaleX(1.1);  opacity: 1; }
          100% { transform: rotate(8deg)  scaleX(0.92); opacity: 0.75; }
        }
        @keyframes beamDrift {
          0%   { left: var(--from); opacity: 0.5; }
          100% { left: var(--to);   opacity: 1; }
        }

        .mino-nav-link { font-family: 'Outfit', sans-serif; font-size: 13px; color: rgba(255,255,255,0.32); cursor: pointer; padding: 5px 12px; border-radius: 6px; transition: all 0.2s; font-weight: 400; }
        .mino-nav-link:hover { color: rgba(255,255,255,0.72); background: rgba(255,255,255,0.06); }

        .section-row { transition: background 0.2s ease; border-radius: 6px; }
        .section-row:hover { background: rgba(255,255,255,0.02) !important; }

        .list-item { transition: border-color 0.2s ease, background 0.2s ease; }
        .list-item:hover { border-color: rgba(255,255,255,0.12) !important; background: rgba(255,255,255,0.03) !important; }
      `}</style>

      <div className="mino-aurora" />
      <div className="mino-dot-grid" />
      <div className="mino-beam" style={{ '--from': '8%',  '--to': '12%', '--dur': '30s' }} />
      <div className="mino-beam" style={{ '--from': '38%', '--to': '42%', '--dur': '38s', opacity: 0.6 }} />
      <div className="mino-beam" style={{ '--from': '68%', '--to': '72%', '--dur': '25s', opacity: 0.4 }} />

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, zIndex: 100, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 48px', background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div onClick={() => navigate('/markets')} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
          <svg width="26" height="26" viewBox="0 0 38 38" fill="none">
            <rect x="1" y="1" width="36" height="36" rx="9" fill="#050505" stroke="#1a1a1e" strokeWidth="1.5"/>
            <line x1="11" y1="30" x2="20" y2="8" stroke="#3772ff" strokeWidth="2.5" strokeLinecap="round"/>
            <line x1="19" y1="30" x2="28" y2="8" stroke="#16c784" strokeWidth="2.5" strokeLinecap="round"/>
          </svg>
          <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 15, fontWeight: 600, letterSpacing: '-0.3px', color: '#e8e6e0' }}>Mino</span>
        </div>
        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {[['Terms', '/terms'], ['Responsible Trading', '/responsible-gambling']].map(([l, p]) => (
            <span key={p} className="mino-nav-link" onClick={() => navigate(p)}>{l}</span>
          ))}
        </div>
        <button onClick={() => navigate(-1)} style={{ background: 'transparent', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 20px', fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 400, cursor: 'pointer', transition: 'all 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#e8e6e0'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.28)'; }}
          onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.4)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          Back
        </button>
      </nav>

      <div style={{ position: 'relative', zIndex: 1, maxWidth: 1000, margin: '0 auto', padding: '0 48px' }}>

        {/* Hero */}
        <div ref={heroRef} style={{ paddingTop: 120, paddingBottom: 100, opacity: heroVisible ? 1 : 0, transform: heroVisible ? 'translateY(0)' : 'translateY(40px)', transition: 'opacity 1s cubic-bezier(0.16,1,0.3,1), transform 1s cubic-bezier(0.16,1,0.3,1)' }}>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 28 }}>Privacy · KDPA 2019 · Updated April 2026</p>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontSize: 'clamp(48px,7vw,88px)', fontWeight: 700, lineHeight: 1.02, letterSpacing: '-3px', color: '#e8e6e0', marginBottom: 36, maxWidth: 720 }}>
            Privacy<br />Policy.
          </h1>
          <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, color: 'rgba(255,255,255,0.36)', lineHeight: 1.8, maxWidth: 480, fontWeight: 300 }}>
            How Mino collects, uses, and protects your personal data. Written in compliance with the Kenya Data Protection Act 2019.
          </p>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* Sections */}
        <div style={{ padding: '60px 0' }}>
          <FadeUp>
            <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11, fontWeight: 500, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: 0 }}>Policy Sections</p>
          </FadeUp>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {sections.map((section, i) => (
              <FadeUp key={section.id} delay={Math.min(i * 0.03, 0.15)}>
                <div className="section-row" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', padding: '48px 8px', display: 'grid', gridTemplateColumns: '72px 1fr', gap: '40px', alignItems: 'start' }}>
                  <div style={{ paddingTop: 3 }}>
                    <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 11, fontWeight: 500, color: 'rgba(255,255,255,0.18)', letterSpacing: '0.08em' }}>{section.id}</span>
                  </div>
                  <div>
                    <h3 style={{ fontFamily: "'Syne', sans-serif", fontSize: 16, fontWeight: 600, color: '#e8e6e0', marginBottom: 14, letterSpacing: '-0.3px' }}>{section.title}</h3>
                    <p style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.34)', fontSize: 14, lineHeight: 1.85, marginBottom: section.list ? 20 : 0, whiteSpace: 'pre-line', fontWeight: 300 }}>{section.content}</p>
                    {section.list && (
                      <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {section.list.map((item, j) => (
                          <li key={j} className="list-item" style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '12px 16px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8 }}>
                            <span style={{ fontFamily: "'Syne', sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.18)', marginTop: 4, flexShrink: 0, fontWeight: 500, letterSpacing: '0.06em' }}>{String(j + 1).padStart(2, '0')}</span>
                            <span style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.4)', fontSize: 13, lineHeight: 1.7, fontWeight: 300 }}>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.footer && (
                      <p style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(255,255,255,0.22)', fontSize: 12, lineHeight: 1.6, marginTop: 20, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.06)', fontWeight: 300 }}>{section.footer}</p>
                    )}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)' }} />

        {/* CTA */}
        <div style={{ padding: '100px 0' }}>
          <FadeUp>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <a href="mailto:support@minomarkets.app" style={{ background: '#e8e6e0', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', transition: 'opacity 0.2s', textDecoration: 'none', display: 'block' }}
                onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
                onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#000', marginBottom: 10, letterSpacing: '-0.5px' }}>Questions about your data?</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.45)', lineHeight: 1.6, marginBottom: 24, fontWeight: 400 }}>We respond within 21 days as required by the KDPA 2019.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  support@minomarkets.app
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </a>
              <div onClick={() => navigate('/terms')} style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16, padding: '40px 36px', cursor: 'pointer', background: 'rgba(255,255,255,0.025)', transition: 'all 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.16)'; e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.09)'; e.currentTarget.style.background = 'rgba(255,255,255,0.025)'; }}>
                <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, fontWeight: 700, color: '#e8e6e0', marginBottom: 10, letterSpacing: '-0.5px' }}>Terms & Conditions</p>
                <p style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.3)', lineHeight: 1.6, marginBottom: 24, fontWeight: 300 }}>Read the full terms governing your use of Mino.</p>
                <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, color: 'rgba(255,255,255,0.28)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  Read terms
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </span>
              </div>
            </div>
          </FadeUp>
        </div>

      </div>

      {/* Footer */}
      <div style={{ position: 'relative', zIndex: 1, borderTop: '1px solid rgba(255,255,255,0.05)', padding: '24px 48px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', fontWeight: 300 }}>© 2026 Mino · Compliant with the Kenya Data Protection Act 2019</span>
        <span onClick={() => navigate('/markets')} style={{ fontFamily: "'Outfit', sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.16)', cursor: 'pointer', fontWeight: 300, transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.48)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.16)'}>
          Back to markets
        </span>
      </div>
    </div>
  );
}